/**
 * Role-Based Access Control (RBAC) Utility
 * 
 * Centralized permission management for the application.
 * Defines role hierarchies and access controls for different modules.
 */

// Role Constants
export const ROLES = {
  SYSTEM_ADMIN: 'System Admin',
  ADMIN: 'Admin',
  DOCUMENTATION_MANAGER: 'Documentation Manager',
  SURVEY_INSPECTOR: 'Survey Inspector',
  CLIENT: 'Client',
  ACCOUNTANT: 'Accountant',
};

// Role groups for easier permission management
export const ROLE_GROUPS = {
  // Full access roles - can view, create, edit, delete
  FULL_ACCESS: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
  
  // Admin-level roles
  ADMINS: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
  
  // Roles that can manage tanks/containers
  TANK_MANAGERS: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
  
  // Roles that have view-only access
  VIEW_ONLY: [ROLES.CLIENT, ROLES.SURVEY_INSPECTOR],
  
  // Roles that cannot see the Client tab in users
  NO_CLIENT_TAB: [ROLES.CLIENT, ROLES.SURVEY_INSPECTOR],
  
  // Roles that can view user management
  USER_VIEWERS: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.ACCOUNTANT],
};

/**
 * Permission definitions for different modules
 */
export const PERMISSIONS = {
  // User Management Permissions
  users: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.ACCOUNTANT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
    viewClientTab: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.ACCOUNTANT],
  },
  
  // Tank Management Permissions
  tanks: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.ACCOUNTANT, ROLES.CLIENT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
  },
  
  // Container Management Permissions
  containers: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.ACCOUNTANT, ROLES.CLIENT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
  },
  
  // Role Management Permissions
  roles: {
    view: [ROLES.SYSTEM_ADMIN],
    create: [ROLES.SYSTEM_ADMIN],
    edit: [ROLES.SYSTEM_ADMIN],
    delete: [ROLES.SYSTEM_ADMIN],
  },
  
  // Billing Management Permissions
  billing: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.ACCOUNTANT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.ACCOUNTANT],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.ACCOUNTANT],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
  },
  
  // Tank Cleaning Permissions
  tankCleaning: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    publish: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadPDF: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    downloadCSV: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    sendEmail: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadFileCabinet: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
  },
  
  // Dry Box Survey Permissions
  dryBoxSurvey: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    publish: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadPDF: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    downloadCSV: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    sendEmail: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadFileCabinet: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
  },
  
  // OnHire Permissions
  onHire: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    publish: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadPDF: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    downloadCSV: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    sendEmail: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadFileCabinet: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
  },
  
  // EI Receipt Permissions
  eiReceipt: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    publish: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadPDF: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    downloadCSV: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    sendEmail: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadFileCabinet: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
  },
  
  // TI Report Permissions
  tiReport: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    publish: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadPDF: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    downloadCSV: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR, ROLES.CLIENT, ROLES.ACCOUNTANT],
    sendEmail: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
    downloadFileCabinet: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR],
  },
  
  // Location Permissions
  locations: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
  },
  
  // Contact Messages Permissions
  contact: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.ACCOUNTANT],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
  },
  
  // Testimonial Permissions
  testimonials: {
    view: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.ACCOUNTANT],
    create: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
    edit: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
    delete: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN],
  },
};

/**
 * Check if a user role has a specific permission
 * @param {string} userRole - The user's role
 * @param {string} module - The module name (e.g., 'users', 'tanks')
 * @param {string} action - The action (e.g., 'view', 'create', 'edit', 'delete')
 * @returns {boolean} - Whether the user has permission
 */
export const hasPermission = (userRole, module, action) => {
  if (!userRole || !module || !action) return false;
  
  const modulePermissions = PERMISSIONS[module];
  if (!modulePermissions) return false;
  
  const allowedRoles = modulePermissions[action];
  if (!allowedRoles) return false;
  
  return allowedRoles.includes(userRole);
};

/**
 * Check if user can view the client tab in user management
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user can see the client tab
 */
export const canViewClientTab = (userRole) => {
  return PERMISSIONS.users.viewClientTab.includes(userRole);
};

/**
 * Check if user has any of the specified roles
 * @param {string} userRole - The user's role
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean} - Whether the user has any of the allowed roles
 */
export const hasAnyRole = (userRole, allowedRoles) => {
  if (!userRole || !allowedRoles || !Array.isArray(allowedRoles)) return false;
  return allowedRoles.includes(userRole);
};

/**
 * Check if user is a view-only user (Client or Survey Inspector for certain modules)
 * @param {string} userRole - The user's role
 * @param {string} module - The module name
 * @returns {boolean} - Whether the user has view-only access
 */
export const isViewOnly = (userRole, module) => {
  if (!userRole || !module) return true;
  
  const modulePermissions = PERMISSIONS[module];
  if (!modulePermissions) return true;
  
  // Check if user can view but not edit or delete
  const canView = modulePermissions.view?.includes(userRole);
  const canEdit = modulePermissions.edit?.includes(userRole);
  const canDelete = modulePermissions.delete?.includes(userRole);
  
  return canView && !canEdit && !canDelete;
};

/**
 * Get user's permission level for a module
 * @param {string} userRole - The user's role
 * @param {string} module - The module name
 * @returns {object} - Object with permission flags
 */
export const getPermissions = (userRole, module) => {
  const modulePermissions = PERMISSIONS[module];
  if (!modulePermissions) {
    return {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canPublish: false,
      canDownloadPDF: false,
      canDownloadCSV: false,
      canSendEmail: false,
      canDownloadFileCabinet: false,
    };
  }
  
  return {
    canView: modulePermissions.view?.includes(userRole) || false,
    canCreate: modulePermissions.create?.includes(userRole) || false,
    canEdit: modulePermissions.edit?.includes(userRole) || false,
    canDelete: modulePermissions.delete?.includes(userRole) || false,
    canPublish: modulePermissions.publish?.includes(userRole) || false,
    canDownloadPDF: modulePermissions.downloadPDF?.includes(userRole) || false,
    canDownloadCSV: modulePermissions.downloadCSV?.includes(userRole) || false,
    canSendEmail: modulePermissions.sendEmail?.includes(userRole) || false,
    canDownloadFileCabinet: modulePermissions.downloadFileCabinet?.includes(userRole) || false,
  };
};

/**
 * Hook-friendly permission getter
 * Use this in components to get all permissions at once
 * @param {string} userRole - The user's role
 * @returns {object} - Object with all module permissions
 */
export const getAllPermissions = (userRole) => {
  const modules = Object.keys(PERMISSIONS);
  const allPermissions = {};
  
  modules.forEach(module => {
    allPermissions[module] = getPermissions(userRole, module);
  });
  
  // Add special permissions
  allPermissions.users.canViewClientTab = canViewClientTab(userRole);
  
  return allPermissions;
};

export default {
  ROLES,
  ROLE_GROUPS,
  PERMISSIONS,
  hasPermission,
  canViewClientTab,
  hasAnyRole,
  isViewOnly,
  getPermissions,
  getAllPermissions,
};
